import { localize, type FieldValidationMetaInfo } from '@vee-validate/i18n'
import { all, required } from '@vee-validate/rules'
import { isEqual } from 'lodash'
import { configure, defineRule } from 'vee-validate'

import {
	dateBetween,
	dateGreaterThanOrEqual,
	getValidDateObject,
	normalizeDate,
} from '@/composables/dateTime'

const isLocalDate = (value: string) => {
	const pattern = new RegExp(
		/^(?:31(.)(?:0?[13578]|1[02])\1|(?:29|30)(.)(?:0?[13-9]|1[0-2])\2)(?:1[6-9]|[2-9]\d)?\d{2}$|^29(.)0?2\3(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:16|[2468][048]|[3579][26])00)$|^(?:0?[1-9]|1\d|2[0-8])(.)(?:0?[1-9]|1[0-2])\4(?:1[6-9]|[2-9]\d)?\d{2}$/
	)

	return pattern.test(value)
}

function hasUniqueValues(stringArray: string[]) {
	// Create a Set from the array. Sets only store unique values.
	const uniqueValues = new Set(stringArray)
	// If the size of the Set is the same as the length of the original array,
	// then all values were unique.
	return uniqueValues.size === stringArray.length
}

type SupportedLanguage = 'de'

type CustomRule = {
	name: string
	rule: (value: any, data?: any, ctx?: FieldValidationMetaInfo) => boolean
	messages: Record<SupportedLanguage, string>
}

function isConditionMet(condition: any, target: any, callback: any) {
	if (typeof callback === 'function') {
		return callback(target)
	}
	return condition
}

function greaterThan(value, { min, condition }) {
	if (!value || !condition || !min) return true
	return value < min
}

function dateGreaterThan(value, { min, condition, target, callback }) {
	if (value && isConditionMet(condition, target, callback) && min) {
		const date = getValidDateObject(value)
		const minDate = getValidDateObject(min)
		return !!date && !!minDate && date.isAfter(minDate)
	}
	return true
}

const Rules: CustomRule[] = [
	{
		name: 'conditionalGreaterThan',
		rule: greaterThan,
		messages: {
			de: '{field} muss größer als 0:{min} sein',
		},
	},
	{
		name: 'date',
		rule: (value) => {
			if (!value) return true
			return getValidDateObject(value) !== undefined
		},
		messages: {
			de: 'Datum ungültig. Bitte überprüfen Sie Ihre Eingabe.',
		},
	},
	{
		name: 'conditionalTimeGreaterThan',
		rule: dateGreaterThan,
		messages: {
			de: '{field} muss hinter der Anfangszeit 0:{min} liegen',
		},
	},
	{
		name: 'conditionalDateGreaterThan',
		rule(value, { min, condition, target, callback }) {
			if (value && isConditionMet(condition, target, callback) && min) {
				const date = getValidDateObject(value)
				const minDate = getValidDateObject(min)
				return (
					date && minDate && normalizeDate(date).isAfter(normalizeDate(minDate))
				)
			}
			return true
		},
		messages: {
			de: '{field} muss hinter dem Anfangsdatum 0:{min} liegen',
		},
	},
	{
		name: 'conditionalDateMin',
		rule(value, { min, condition, target, callback }) {
			if (value && isConditionMet(condition, target, callback) && min) {
				const date = getValidDateObject(value)
				const minDate = getValidDateObject(min)

				return (
					date &&
					minDate &&
					dateGreaterThanOrEqual(normalizeDate(date), normalizeDate(minDate))
				)
			}
			return true
		},
		messages: {
			de: '{field} muss dem 0:{min} entsprechen oder dahinter liegen',
		},
	},
	{
		name: 'conditionalDateMax',
		rule(value, { max, condition, target, callback }) {
			if (value && isConditionMet(condition, target, callback) && max) {
				const date = getValidDateObject(value)
				const maxDate = getValidDateObject(max)

				return (
					date &&
					maxDate &&
					(dateGreaterThanOrEqual(normalizeDate(maxDate), normalizeDate(date)) ||
					normalizeDate(date).isSame(normalizeDate(maxDate), 'day'))
				)
			}
			return true
		},
		messages: {
			de: '{field} muss dem 0:{max} entsprechen oder davor liegen',
		},
	},
	{
		name: 'count',
		rule(value, { count }) {
			if (Array.isArray(value) && typeof count === 'number')
				return value.length <= count
			return true
		},
		messages: {
			de: '{field} darf maximal 0:{count} Elemente haben',
		},
	},
	{
		name: 'countBetween',
		rule(value, { count, min }) {
			if (
				Array.isArray(value) &&
				typeof count === 'number' &&
				typeof min === 'number'
			)
				return value.length <= count && value.length >= min
			return true
		},
		messages: {
			de: '{field} darf maximal 0:{count} und muss mindestens 1:{min} Elemente haben',
		},
	},
	{
		name: 'dateBetween',
		rule(value, { min, max }) {
			if (typeof min === 'string' && typeof max === 'string') {
				const valueDate = getValidDateObject(value)
				const minDate = getValidDateObject(min)
				const maxDate = getValidDateObject(max)
				return (
					valueDate &&
					minDate &&
					maxDate &&
					dateBetween(valueDate, minDate, maxDate)
				)
			}
			return true
		},
		messages: {
			de: `{field} muss zwischen 0:{min} und 1:{max} liegen`,
		},
	},
	{
		name: 'dateFormat',
		rule(value) {
			if (!value) return true
			return isLocalDate(value)
		},
		messages: {
			de: '{field} muss ein gültiges Datum sein',
		},
	},
	{
		name: 'timeFormat',
		rule(value) {
			const pattern = new RegExp(/^(0\d|1\d|2[0-3]):[0-5]\d$/)
			return pattern.test(value)
		},
		messages: {
			de: '{field} muss eine gültige Uhrzeit sein',
		},
	},
	{
		name: 'price',
		rule(value) {
			const pattern = new RegExp(/^(?:\d*)?(?:,\d{2})?$/)
			return pattern.test(value)
		},
		messages: {
			de: '{field} muss ein gültiger Betrag sein',
		},
	},
	{
		name: 'unique',
		rule(value, [items, name]) {
			if (!(items || name)) return true
			return (
				items.filter((item) => item[name].toLowerCase() === value.toLowerCase())
					.length === 0
			)
		},
		messages: {
			de: '{field} wird bereits verwendet',
		},
	},
	{
		name: 'sameValueAs',
		rule: (value, [target]) => {
			if (!value || !target) return true
			return isEqual(value, target)
		},
		messages: {
			de: 'Die werte müssen übereinstimmen',
		},
	},
	{
		name: 'requiredElse',
		rule: (value, [target]) => {
			if (required(target)) return true

			return required(value)
		},
		messages: {
			de: '{field} ist ein Pflichtfeld',
		},
	},
	{
		name: 'requiredIfCallback',
		rule: (value, { target, callback }) => {
			if (!target) return true
			return callback(target) ? required(value) : true
		},
		messages: {
			de: '{field} ist ein Pflichtfeld',
		},
	},
	{
		name: 'fileMaxSize',
		rule: (value: File | File[], [size]) => {
			if (!value || !size || (Array.isArray(value) && value.length <= 0))
				return true
			const sizeValue = typeof size === 'string' ? parseInt(size) : size

			if (Array.isArray(value)) {
				const totalSize = value.reduce((sum, file) => sum + file.size, 0)
				return totalSize < sizeValue
			} else {
				return value.size < sizeValue
			}
		},
		messages: {
			de: 'Die maximale Dateigröße wird überschritten',
		},
	},
	{
		name: 'fileMaxCount',
		rule: (value: File | File[], [total]) => {
			if (!value || !total || (Array.isArray(value) && value.length <= 0))
				return true
			const sizeValue = typeof total === 'string' ? parseInt(total) : total

			if (Array.isArray(value)) {
				return value.length <= sizeValue
			} else {
				return true
			}
		},
		messages: {
			de: 'Die maximale Anzahl der Dateiein (0:{total}) wird überschritten',
		},
	},
	{
		name: 'fileUnique',
		rule: (value: File | File[]) => {
			if (!value || (Array.isArray(value) && value.length <= 0)) return true

			if (Array.isArray(value)) {
				const names = value.map((file) => file.name)
				return hasUniqueValues(names)
			} else {
				return true
			}
		},
		messages: {
			de: 'In der Auswahl befinden sich doppelte Dateien.',
		},
	},
	{
		name: 'fileTypes',
		rule: (value: File | File[], [acceptedFiles]) => {
			if (
				!value ||
				!acceptedFiles ||
				(Array.isArray(value) && value.length <= 0)
			)
				return true

			const types = acceptedFiles?.split(',').map((item: string) => {
				const re = new RegExp('\\.(\\w+)')
				const result = item.match(re)
				if (result) {
					return result[1]
				}
				return item
			})

			if (Array.isArray(value) && Array.isArray(types)) {
				return value.every((file) => {
					return types.some((type) => {
						const re = new RegExp(`.*${type}`)
						return re.test(file.type)
					})
				})
			} else {
				return types.some((type) => {
					const re = new RegExp(`.*${type}`)
					return re.test(value.type)
				})
			}
		},
		messages: {
			de: 'Ihre Dateiauswahl enthält nicht erlaubte Dateitypen.',
		},
	},
]

Rules.forEach(({ name, rule, messages }) => {
	defineRule(name, rule)

	Object.entries(messages).forEach(([local, message]) => {
		configure({
			generateMessage: localize(local, {
				messages: {
					[name]: message,
				},
			}),
		})
	})
})

Object.entries(all).forEach(([name, rule]) => {
	defineRule(name, rule)
})
